import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { Add as AddIcon, Close as CloseIcon } from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';
import { State, WorkflowInvoke } from '@sierralabs/workflow';

import { useJiraConnectContext } from '../../../jira-connect/JiraConnect.context';
import {
  useGetJiraIssueTransitions,
  useTransitionJiraIssue
} from '../../../jira-connect/JiraConnect.hooks';
import { JiraIssueTransition } from '../../../jira-connect/JiraConnect.interface';
import JiraIssueTypePicker from '../../../jira-connect/pickers/JiraIssueTypePicker';
import JiraProjectPicker from '../../../jira-connect/pickers/JiraProjectPicker';
import JiraStatusPicker from '../../../jira-connect/pickers/JiraStatusPicker';
import {
  TaskContext,
  TransitionJiraIssueParams
} from '../../../tasks/Task.machine';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin
} from '../ServicePlugin';

const StyledConfiguratorDiv = styled.div`
  .field {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  .event-field {
    margin-top: 12px;
  }
  .MuiTableHead-root .MuiTableCell-root {
    padding-left: 4px;
    padding-right: 4px;
  }
  .MuiTableRow-root.dense .MuiTableCell-root {
    padding: 2px 4px;
  }
`;

const TransitionJiraIssueConfigurator: React.FC<
  ServiceConfiguratorProps
> = props => {
  const { onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  if (!invoke.data) invoke.data = {};
  const data: TransitionJiraIssueParams['data'] = invoke.data;
  const issues = data.issues || [];
  const disabled = !data.projectId;

  const update = (data: TransitionJiraIssueParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  return (
    <StyledConfiguratorDiv>
      <JiraProjectPicker
        value={data.projectId}
        textFieldProps={{
          size: 'small',
          label: 'Project',
          placeholder: 'Select Project'
        }}
        onChange={projectId => update({ ...data, projectId, issues: [] })}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '40%' }}>Issue Type</TableCell>
              <TableCell>Statuses</TableCell>
              <TableCell style={{ width: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {issues.length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  {data.projectId ? (
                    'No items added.'
                  ) : (
                    <>
                      Select a <strong>Project</strong> to add items.
                    </>
                  )}
                </TableCell>
              </TableRow>
            )}
            {issues.map((issue, index) => (
              <TableRow key={index} className='dense'>
                <TableCell>
                  <JiraIssueTypePicker
                    value={issue.issueType?.id}
                    projectId={data.projectId}
                    disabled={disabled}
                    textFieldProps={{
                      size: 'small',
                      label: undefined,
                      placeholder: 'Select Issue Type'
                    }}
                    onChange={issueType => {
                      issues[index].issueType = issueType;
                      issues[index].statuses = undefined;
                      update({ ...data, issues });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <JiraStatusPicker
                    multiple
                    projectId={data.projectId || ''}
                    issueTypeId={issue.issueType?.id || ''}
                    disabled={disabled || !issue.issueType?.id}
                    value={(issue.statuses || []).map(status => status.id)}
                    textFieldProps={{
                      size: 'small',
                      label: undefined,
                      placeholder: 'Select Statuses'
                    }}
                    onChange={statuses => {
                      issues[index].statuses = statuses;
                      update({ ...data, issues });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size='small'
                    disabled={disabled}
                    onClick={() => {
                      issues.splice(index, 1);
                      update({ ...data, issues });
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box mt={2}>
        <Button
          variant='outlined'
          disabled={disabled}
          startIcon={<AddIcon />}
          onClick={() => {
            issues.push({});
            update({ ...data, issues });
          }}
        >
          Add Item
        </Button>
      </Box>
      <Box mt={1}>
        <Typography variant='caption'>
          Once the Workflow status is reached, the linked Jira issues will
          transition to its first available selected status.
        </Typography>
      </Box>
    </StyledConfiguratorDiv>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  .icon {
    font-size: 18px;
    margin-top: -3px;
    vertical-align: middle;
  }
`;

const TransitionJiraIssueDisplay: React.FC<ServiceDisplayProps> = props => {
  const { invoke } = props;
  const data: TransitionJiraIssueParams['data'] | undefined = invoke.data;
  const issues = data?.issues || [];
  return !data ? (
    <></>
  ) : (
    <StyledDisplayDiv>
      <div>
        Issues:{' '}
        {issues
          .map(issues => {
            const issueType = issues.issueType?.label;
            const status = (issues.statuses || [])
              .map(status => status.name)
              .join(', ');
            return `${issueType} (${status})`;
          })
          .join(', ') || '?'}
      </div>
    </StyledDisplayDiv>
  );
};

const StyledDialogContent = styled(DialogContent)`
  .MuiTypography-root,
  .MuiAlert-root {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

interface TransitionJiraIssueDialogProps {
  open: boolean;
  data: TransitionJiraIssueParams['data'];
  state: State<TaskContext>;
  autoSkip?: boolean;
  onCancel?: () => void;
  onSuccess?: () => void;
}

const TransitionJiraIssueDialog: React.FC<
  TransitionJiraIssueDialogProps
> = props => {
  const { open, data, state, autoSkip, onCancel, onSuccess } = props;
  const stateRef = useRef(state);
  stateRef.current = state;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const [total, setTotal] = useState({ done: 0, skipped: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<React.ReactNode>();
  const { isAvailable } = useJiraConnectContext();
  const [getTransitionsResult, getJiraIssueTransitions] =
    useGetJiraIssueTransitions();
  const [transitionResult, transitionJiraIssue] = useTransitionJiraIssue();
  const task = stateRef.current.context.task;
  const alertMsg = !isAvailable
    ? 'Unable to run action and transition Jira issue(s). ' +
      'You can choose to skip this action, or open SierraQMS ' +
      'within JIRA and try again to properly run this action.'
    : alertMessage;
  const loading =
    isLoading || getTransitionsResult.isLoading || transitionResult.isLoading;
  const filteredJiraIssues = task.jiraIssues?.filter(jiraIssue => {
    return data.issues.some(
      item => item.issueType?.id === jiraIssue.issue.fields.issuetype.id
    );
  });
  const jiraIssuesRef = useRef(filteredJiraIssues);
  jiraIssuesRef.current = filteredJiraIssues;

  const handleCancel = () => {
    if (!loading) {
      onCancel?.();
    }
  };

  useEffect(() => {
    const dataIssues = data.issues || [];
    if (!isAvailable || dataIssues.length === 0) {
      return;
    }

    const run = async () => {
      const selectedIssueTypes = dataIssues
        .map(item => `"${item.issueType?.label}"`)
        .join(', ');
      const filteredJiraIssues = jiraIssuesRef.current;
      if (!filteredJiraIssues || filteredJiraIssues.length === 0) {
        setAlertMessage(
          'There are no linked JIRA issues that match some of the ' +
            `issue type(s): ${selectedIssueTypes}, please link an issue that ` +
            'matches the given issue type(s) to proceed. You can choose to skip this action.'
        );
        return;
      }

      setIsLoading(true);
      setAlertMessage(undefined);
      const promises = filteredJiraIssues.map(async issue => {
        const result = await getJiraIssueTransitions(issue.key);
        const transitions = result?.transitions || [];

        const matchedIssues = dataIssues.filter(
          field => field.issueType?.id === issue.issue.fields.issuetype.id
        );
        // get transition based on configuration
        let transition: JiraIssueTransition | undefined;
        for (const field of matchedIssues) {
          if (transition) {
            break;
          } else if (field.issueType?.id !== issue.issue.fields.issuetype.id) {
            continue;
          }
          for (const status of field.statuses || []) {
            transition = transitions.find(
              transition => transition.to.id === status.id
            );
            if (transition) {
              break;
            }
          }
        }

        if (transition) {
          // perform transition on this issue
          await transitionJiraIssue(issue.key, transition.id);
          console.debug(
            '[transitionJiraIssue] Issue: %o Transition ID: %o Name: %o',
            issue.key,
            transition.id,
            transition.name
          );
          setTotal(total => ({ ...total, done: total.done + 1 }));
        } else {
          setTotal(total => ({ ...total, skipped: total.skipped + 1 }));
        }
      });
      await Promise.all(promises);

      // once done, run onSuccess callback
      setIsLoading(false);
      onSuccessRef.current?.();
    };
    run();
  }, [data, isAvailable, getJiraIssueTransitions, transitionJiraIssue]);

  // auto skip if alert message is set
  useEffect(() => {
    if (alertMsg && autoSkip) {
      onSuccessRef.current?.();
    }
  }, [alertMsg, autoSkip]);

  return (
    <Dialog open={open} onClose={handleCancel}>
      {loading && <LinearProgress />}
      <DialogTitle>Transition Jira Issue</DialogTitle>
      <StyledDialogContent>
        {loading && (
          <>
            <Typography>Transitioning Jira Issue(s)...</Typography>
            <Typography>
              <strong>{total.done}</strong> (Done) and{' '}
              <strong>{total.skipped}</strong> (Skipped) of{' '}
              <strong>{filteredJiraIssues?.length || 0}</strong> issue(s)
            </Typography>
          </>
        )}
        {alertMsg && (
          <Alert severity='warning'>
            <AlertTitle>
              {!isAvailable
                ? 'JIRA Connect API is not available'
                : 'Unable to update JIRA issue(s)'}
            </AlertTitle>
            {alertMsg}
          </Alert>
        )}
      </StyledDialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={handleCancel}>
          Cancel
        </Button>
        {alertMsg && (
          <Button
            color='primary'
            variant='outlined'
            disabled={loading}
            onClick={() => onSuccess?.()}
          >
            Skip Action and Proceed
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const TransitionJiraIssuePlugin: ServicePlugin<TransitionJiraIssueParams> =
  {
    src: 'transitionJiraIssue',
    name: 'Transition Jira Issue',
    configurator: TransitionJiraIssueConfigurator,
    display: TransitionJiraIssueDisplay,
    validate: (condition: Partial<WorkflowInvoke>) => {
      const data = condition.data as TransitionJiraIssueParams['data'];
      return !!(
        data &&
        data.projectId &&
        Array.isArray(data.issues) &&
        data.issues.length > 0 &&
        data.issues.every(issue => {
          return (
            issue.issueType &&
            Array.isArray(issue.statuses) &&
            issue.statuses.length > 0
          );
        })
      );
    },
    service: (
      state: State<TaskContext>,
      params: TransitionJiraIssueParams[] | TransitionJiraIssueParams,
      render
    ) => {
      console.log('transitionJiraIssue params', state, params);
      if (
        state.isSimulation ||
        state.context.skipServicePlugins?.includes(
          TransitionJiraIssuePlugin.src
        )
      ) {
        return true;
      }
      const pluginParams = Array.isArray(params) ? params[0] : params;
      return new Promise((resolve, reject) => {
        render?.(
          <TransitionJiraIssueDialog
            open
            data={pluginParams.data}
            state={state}
            autoSkip={state.context.autoSkipJiraPlugins?.includes(
              TransitionJiraIssuePlugin.src
            )}
            onSuccess={() => {
              render?.(undefined);
              resolve(true);
            }}
            onCancel={() => {
              render?.(undefined);
              reject(true);
            }}
          />
        );
      });
    }
  };
